import {
  ErrorHandler,
  importProvidersFrom,
  provideAppInitializer,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';

import * as Sentry from '@sentry/angular';

import { environment } from './environments/environment';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { SplashScreen } from '@awesome-cordova-plugins/splash-screen/ngx';
import { RouteReuseStrategy, Router, RouterModule, provideRouter } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { EvsApiHeaderInterceptor } from '@commons/services/EvsApiHeader.interceptor';
import { EvsResponseInterceptor } from '@commons/services/EvsResponse.interceptor';
import { UserSessionInterceptor } from './app/app.services';
import { AppErrorHandler } from './app/app.errorHandler';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Camera } from '@awesome-cordova-plugins/camera/ngx';
import { FileTransfer } from '@awesome-cordova-plugins/file-transfer/ngx';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { Device } from '@awesome-cordova-plugins/device/ngx';
import { Adjust } from '@awesome-cordova-plugins/adjust/ngx';
import { Deeplinks } from '@awesome-cordova-plugins/deeplinks/ngx';
import { Market } from '@awesome-cordova-plugins/market/ngx';
import { LaunchReview } from '@awesome-cordova-plugins/launch-review/ngx';
import { CameFromRouteService } from './pages/tunnel-paiement-refonte/services/came-from-route.service';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { routes } from './app/app.routes';
import { FormsModule } from '@angular/forms';
import { AppComponent } from './app/app.component';

import { provideIonicAngular, IonicRouteStrategy } from '@ionic/angular/standalone';
import { IonicStorageModule } from '@ionic/storage-angular';
import { EvsTheme, provideEvsKit } from '@en-voiture-simone/evs-kit';
import { WebView } from '@awesome-cordova-plugins/ionic-webview/ngx';
import { FeatureFlagsService } from '@app/services/feature-flags.service';
import { KameleoonFeatureFlagsService } from '@app/services/kameleoon-feature-flags.service';
import { InitializationService } from './app/services/initialization.service';

import moment from 'moment-timezone';
import 'moment/locale/fr';

Sentry.init({
  dsn: environment.sentryAddr,
  environment: environment.env,
  tracesSampleRate: 0.1,
});

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideAppInitializer(() => {
      moment.locale('fr');

      const cameFromRouteService = inject(CameFromRouteService);
      cameFromRouteService.getCameFromUrl();

      const featureFlagsService = inject(FeatureFlagsService);
      const kameleoonFeatureFlagsService = inject(KameleoonFeatureFlagsService);
      featureFlagsService.registerProvider(kameleoonFeatureFlagsService);

      // Register all init services
      const initializationService = inject(InitializationService);
      initializationService.registerServices();
    }),
    provideRouter(routes),
    importProvidersFrom(BrowserModule, RouterModule, IonicStorageModule.forRoot(), FormsModule),
    StatusBar,
    SplashScreen,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EvsApiHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EvsResponseInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UserSessionInterceptor,
      multi: true,
    },
    { provide: 'googleTagManagerId', useValue: environment.gtmId },
    { provide: ErrorHandler, useClass: AppErrorHandler },
    { provide: Sentry.TraceService, deps: [Router] },
    InAppBrowser,
    Camera,
    FileTransfer,
    File,
    Device,
    Adjust,
    Deeplinks,
    Market,
    LaunchReview,
    WebView,
    provideHttpClient(withInterceptorsFromDi()),
    provideIonicAngular({}),
    provideEvsKit({ theme: EvsTheme.EVS2026 }),
  ],
})
  .then((ref) => {
    // Ensure Angular destroys itself on hot reloads.
    if (window['ngRef']) {
      window['ngRef'].destroy();
    }
    window['ngRef'] = ref;
  })
  .catch((err) => {
    throw new Error('main.ts bootstrap error: ' + JSON.stringify(err));
  });
